'use client';
import { AmexIcon } from '@curology/ui-components-web/react/Icon/icons/AmexIcon';
import { AppleIcon } from '@curology/ui-components-web/react/Icon/icons/AppleIcon';
import { DinersClubIcon } from '@curology/ui-components-web/react/Icon/icons/DinersClubIcon';
import { DiscoverIcon } from '@curology/ui-components-web/react/Icon/icons/DiscoverIcon';
import { JcbIcon } from '@curology/ui-components-web/react/Icon/icons/JcbIcon';
import { MastercardIcon } from '@curology/ui-components-web/react/Icon/icons/MastercardIcon';
import { PaymentIcon } from '@curology/ui-components-web/react/Icon/icons/PaymentIcon';
import { PayPalIcon } from '@curology/ui-components-web/react/Icon/icons/PayPalIcon';
import { VisaIcon } from '@curology/ui-components-web/react/Icon/icons/VisaIcon';
import React, { type ForwardRefExoticComponent } from 'react';

import { DISPLAY_PAYMENT_TYPES, PAYMENT_TYPES } from '@/consts/payments';
import { type PaymentMethod } from '@/services/resubscriptionService/types';
import { isDefined } from '@/utils/isDefined';

interface IconData {
  icon: ForwardRefExoticComponent<any>;
  size: 'md' | 'xl';
}

const iconMap = {
  [DISPLAY_PAYMENT_TYPES.AMERICAN_EXPRESS]: { icon: AmexIcon, size: 'xl' },
  [DISPLAY_PAYMENT_TYPES.APPLE_PAY]: { icon: AppleIcon, size: 'md' },
  [DISPLAY_PAYMENT_TYPES.DINERS_CLUB]: { icon: DinersClubIcon, size: 'md' },
  [DISPLAY_PAYMENT_TYPES.DISCOVER]: { icon: DiscoverIcon, size: 'xl' },
  [DISPLAY_PAYMENT_TYPES.JCB]: { icon: JcbIcon, size: 'xl' },
  [DISPLAY_PAYMENT_TYPES.MASTERCARD]: { icon: MastercardIcon, size: 'md' },
  [DISPLAY_PAYMENT_TYPES.PAYPAL]: { icon: PayPalIcon, size: 'md' },
  [DISPLAY_PAYMENT_TYPES.UNION_PAY]: { icon: PaymentIcon, size: 'md' },
  [DISPLAY_PAYMENT_TYPES.VISA]: { icon: VisaIcon, size: 'xl' },
  [DISPLAY_PAYMENT_TYPES.VISA_LEGACY]: { icon: VisaIcon, size: 'xl' },
} satisfies {
  [k in valueof<typeof DISPLAY_PAYMENT_TYPES>]: IconData;
};

const defaultDisplayTextMap = {
  [PAYMENT_TYPES.CREDIT_CARD]: 'Card',
  [PAYMENT_TYPES.PAYPAL]: 'PayPal™',
  [PAYMENT_TYPES.APPLE_PAY]: 'ApplePay™',
} satisfies {
  [k in valueof<typeof PAYMENT_TYPES>]: string;
};

const getIcon = (
  type: valueof<typeof PAYMENT_TYPES>,
  cardBrand?: string,
): IconData => {
  const typeKey = type as valueof<typeof DISPLAY_PAYMENT_TYPES>;

  if (isDefined(iconMap[typeKey])) {
    return iconMap[typeKey];
  }

  const cardBrandKey = cardBrand
    ? (cardBrand as valueof<typeof DISPLAY_PAYMENT_TYPES>)
    : null;

  return cardBrandKey && isDefined(iconMap[cardBrandKey])
    ? iconMap[cardBrandKey]
    : { icon: PaymentIcon, size: 'md' };
};

const PaymentMethodSummary = ({
  paymentMethod,
}: {
  paymentMethod: PaymentMethod | null;
}) => {
  if (!paymentMethod) {
    return <span className="cur-text-error">Add payment method</span>;
  }

  const { type, cardBrand, cardLastFour } = paymentMethod;

  const { icon: Icon, size } = getIcon(type, cardBrand);

  const getSummaryText = (): string => {
    switch (true) {
      case type === PAYMENT_TYPES.PAYPAL:
        return defaultDisplayTextMap[PAYMENT_TYPES.PAYPAL];
      case isDefined(cardLastFour):
        return `Card ending in ${cardLastFour}`;
      case type === PAYMENT_TYPES.APPLE_PAY:
        return defaultDisplayTextMap[PAYMENT_TYPES.APPLE_PAY];
      case type === PAYMENT_TYPES.CREDIT_CARD:
        return defaultDisplayTextMap[PAYMENT_TYPES.CREDIT_CARD];
      default:
        return 'Update your payment method';
    }
  };

  return (
    <div className="cur-flex cur-items-center cur-h-7 data-hj-suppress">
      <Icon size={size} className="cur-mr-2" /> {getSummaryText()}
    </div>
  );
};

export default PaymentMethodSummary;
